import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import logo from "../../../assets/logo.png";
import L from "../../../constants/baysportLabels";
import { LegalFooterLinks } from "../../Legal/LegalDocLink";
import { TRIAL_DAYS } from "../../../constants/trial";
import {
  buildLandingPriceHighlights,
  PLAN_POSITIONING,
  LANDING_PLANS_FALLBACK,
} from "../../../constants/planMarketing";
import platformAPI from "../../../services/platformApi";
import { getDashboardPath } from "../../../utils/authRedirect";
import { buildClinicPortalUrl } from "../../../utils/portalHost";
import { hasAdminAccess } from "../../../utils/roles";
import { trackTryClick } from "../../../analytics/metrikaGoals";
import { yandexMetrikaParams } from "../../../analytics/yandexMetrika";
import {
  TRAINER_LANDING_PAGE_TITLE,
  TRAINER_LANDING_PAGE_DESCRIPTION,
  TRAINER_LANDING_PAGE_KEYWORDS,
  TRAINER_LANDING_PATH,
  LANDING_OG_IMAGE,
  LANDING_OG_IMAGE_ALT,
  buildTrainerLandingJsonLd,
} from "../HomeLanding/landingSeo";
import { SITE_ORIGIN } from "../../../config/site";
import Preloader from "../../common/Preloader";
import Seo from "../../Seo";
import s from "./yandexLanding.module.css";

function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

const HERO_POINTS = [
  {
    icon: "+",
    text: "Ученики, абонементы и посещения — на телефоне, прямо на тренировке",
  },
  {
    icon: "◎",
    text: "Родители сами видят расписание, посещения и остаток занятий",
  },
  {
    icon: "₽",
    text: "Можно подключить онлайн-оплату — родители платят за абонемент картой",
  },
  {
    icon: "✓",
    text: `От 790 ₽ в месяц · ${TRIAL_DAYS} дней бесплатно, карту указывать не нужно`,
  },
];

const WAYS = [
  {
    id: "journal",
    art: "journal",
    tone: "lilac",
    badge: "На занятии",
    title: "Отметьте группу за минуту",
    text: "Открыли занятие, поставили отметки — абонемент обновился сам.",
  },
  {
    id: "parents",
    art: "parents",
    tone: "rose",
    badge: "Родители",
    title: "Кабинет вместо общего чата",
    text: "Расписание, кто был и сколько занятий осталось — в одном месте.",
  },
  {
    id: "solo",
    art: "solo",
    tone: "sky",
    badge: "Один тренер",
    title: "Без команды внедренцев",
    text: "Создали секцию, добавили группу — можно работать уже сегодня.",
  },
];

function WayArt({ kind }) {
  if (kind === "parents") {
    return (
      <div className={s.wayArt} aria-hidden>
        <div className={`${s.wayDevice} ${s.wayDeviceParents}`}>
          <span className={s.wayDeviceBar} />
          <div className={s.wayDeviceHead}>Кабинет</div>
          <div className={s.wayParentCard}>
            <strong>Аня, 8 лет</strong>
            <span>Сегодня · была</span>
            <em>осталось 6 занятий</em>
          </div>
          <div className={s.wayLink}>Открыть</div>
        </div>
      </div>
    );
  }
  if (kind === "solo") {
    return (
      <div className={s.wayArt} aria-hidden>
        <div className={`${s.wayDevice} ${s.wayDeviceSolo}`}>
          <span className={s.wayDeviceBar} />
          <div className={s.wayDeviceHead}>Группы</div>
          <div className={s.waySoloRows}>
            <span>
              <b>Младшая</b>
              <small>12 человек</small>
            </span>
            <span>
              <b>Средняя</b>
              <small>9 человек</small>
            </span>
          </div>
          <div className={s.waySoloAdd}>+ Новая группа</div>
        </div>
      </div>
    );
  }
  return (
    <div className={s.wayArt} aria-hidden>
      <div className={`${s.wayDevice} ${s.wayDeviceJournal}`}>
        <span className={s.wayDeviceBar} />
        <div className={s.wayDeviceHead}>Журнал · сегодня</div>
        <div className={s.wayAttend}>
          <span>
            <i>✓</i>
            <b>Иванов</b>
          </span>
          <span>
            <i>✓</i>
            <b>Петрова</b>
          </span>
          <span className={s.wayAttendMiss}>
            <i />
            <b>Сидоров</b>
          </span>
        </div>
      </div>
    </div>
  );
}

const ADVANTAGES = [
  {
    art: "routine",
    title: "Меньше рутины после тренировки",
    text: "Отметили группу на телефоне — и всё. Не нужно вечером переносить записи из тетради в таблицу.",
  },
  {
    art: "parents",
    title: "Родители меньше пишут в мессенджер",
    text: "Расписание, кто был на занятии и сколько осталось занятий — уже в кабинете секции.",
  },
  {
    art: "pay",
    title: "Онлайн-оплата абонементов",
    text: "Подключите приём платежей: родители оплатят абонемент картой в кабинете, без переводов «на карту».",
  },
];

function AdvArt({ kind }) {
  if (kind === "parents") {
    return (
      <div className={`${s.advArt} ${s.advArtParents}`} aria-hidden>
        <div className={s.advPanel}>
          <div className={s.advPanelHead}>Кабинет</div>
          <div className={s.advPanelRows}>
            <span />
            <span />
          </div>
          <div className={s.advStatus}>
            <i>✓</i>
            <em>был на занятии</em>
          </div>
        </div>
      </div>
    );
  }
  if (kind === "pay") {
    return (
      <div className={`${s.advArt} ${s.advArtPay}`} aria-hidden>
        <div className={s.advPanel}>
          <div className={s.advPanelHead}>Абонемент</div>
          <strong className={s.advPrice}>1 490 ₽</strong>
          <div className={s.advPayBtn}>Оплатить картой</div>
        </div>
      </div>
    );
  }
  return (
    <div className={`${s.advArt} ${s.advArtRoutine}`} aria-hidden>
      <div className={s.advPanel}>
        <div className={s.advPanelHead}>Журнал</div>
        <div className={s.advRows}>
          <span className={s.advRowOn}>
            <i>✓</i>
            <b />
          </span>
          <span className={s.advRowOn}>
            <i>✓</i>
            <b />
          </span>
          <span className={s.advRowOff}>
            <i />
            <b />
          </span>
        </div>
      </div>
    </div>
  );
}

const FAQ = [
  {
    q: "Подойдёт, если я веду секцию один?",
    a: "Да. Бай.Спорт — CRM для одного тренера: посещения, абонементы, расписание и кабинет родителей — без отдельного администратора.",
  },
  {
    q: "Нужен ли компьютер в зале?",
    a: "Нет. Всё открывается в браузере телефона. Страницу можно сохранить на главный экран — как обычное приложение.",
  },
  {
    q: "Что происходит с абонементом после отметки?",
    a: "Занятие списывается автоматически. Вы и родитель сразу видите, сколько занятий осталось.",
  },
  {
    q: "Родители сами видят посещения?",
    a: "Да. В кабинете секции — расписание, кто был на занятии и остаток абонемента. Приложение из магазина не нужно.",
  },
  {
    q: "Можно принимать оплату за абонементы онлайн?",
    a: "Да. Подключаете эквайринг (ЮKassa или T-Bank) — родители оплачивают абонемент картой в кабинете. Деньги приходят на ваш счёт.",
  },
  {
    q: "Сколько это стоит?",
    a: `Первые ${TRIAL_DAYS} дней бесплатно, карту указывать не нужно. Потом — от 790 ₽ в месяц. Если учеников станет больше, тариф можно сменить, данные сохранятся.`,
  },
];

const NAV_LINKS = [
  { id: "ways", label: "Возможности" },
  { id: "advantages", label: "Преимущества" },
  { id: "pricing", label: "Тарифы" },
];

export default function YandexTrainerLanding() {
  const { isAuth, role, tenant } = useSelector((state) => state.auth);
  const hasClub = isAuth && tenant?.subdomain && hasAdminAccess(role);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [activeNav, setActiveNav] = useState("ways");

  useEffect(() => {
    yandexMetrikaParams({ landing: "trainer" });
  }, []);

  useEffect(() => {
    platformAPI
      .getPublicPlans()
      .then((r) => {
        const loaded = Array.isArray(r?.plans) ? r.plans : [];
        setPlans(loaded.length ? loaded : LANDING_PLANS_FALLBACK);
      })
      .catch(() => setPlans(LANDING_PLANS_FALLBACK))
      .finally(() => setPlansLoading(false));
  }, []);

  useEffect(() => {
    const ids = NAV_LINKS.map((item) => item.id);
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (!elements.length) return undefined;

    const visibility = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibility.set(entry.target.id, entry.intersectionRatio);
        });
        let bestId = ids[0];
        let bestRatio = -1;
        ids.forEach((id) => {
          const ratio = visibility.get(id) || 0;
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        });
        if (bestRatio > 0) setActiveNav(bestId);
      },
      {
        root: null,
        rootMargin: "-20% 0px -55% 0px",
        threshold: [0, 0.15, 0.35, 0.55, 0.75, 1],
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const featuredCode = useMemo(() => "pro", []);

  const onTry = () => trackTryClick({ landing: "trainer" });

  const onNavClick = (id) => {
    setActiveNav(id);
  };

  return (
    <div className={s.page}>
      <Seo
        title={TRAINER_LANDING_PAGE_TITLE}
        description={TRAINER_LANDING_PAGE_DESCRIPTION}
        keywords={TRAINER_LANDING_PAGE_KEYWORDS}
        canonicalUrl={`${SITE_ORIGIN}${TRAINER_LANDING_PATH}`}
        ogImage={LANDING_OG_IMAGE}
        ogImageAlt={LANDING_OG_IMAGE_ALT}
        jsonLd={buildTrainerLandingJsonLd(FAQ)}
      />

      {hasClub ? (
        <div className={s.sessionBar}>
          <span>
            Вы вошли в <strong>{tenant.name || tenant.subdomain}</strong>
          </span>
          <a
            href={buildClinicPortalUrl(tenant.subdomain, getDashboardPath(role))}
            className={`${s.btn} ${s.btnDark}`}
          >
            Открыть кабинет
          </a>
        </div>
      ) : null}

      <header className={s.navWrap}>
        <div className={s.nav}>
          <Link to="/" className={s.navBrand}>
            <img src={logo} alt="" className={s.navLogo} />
            <span className={s.navBrandText}>{L.productName}</span>
          </Link>
          <nav className={s.navLinks} aria-label="Разделы">
            {NAV_LINKS.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={`${s.navPill} ${
                  activeNav === item.id ? s.navPillOn : ""
                }`}
                onClick={() => onNavClick(item.id)}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className={s.navActions}>
            <Link
              to="/register-clinic"
              className={`${s.btn} ${s.btnDark}`}
              onClick={onTry}
            >
              Попробовать
            </Link>
            <Link to="/account/login" className={`${s.btn} ${s.btnDark}`}>
              Войти
            </Link>
          </div>
        </div>
      </header>

      <section className={s.hero}>
        <div className={s.heroInner}>
          <div className={s.heroCopy}>
            <h1 className={s.heroTitle}>
              CRM для тренера — без тетради и Excel
            </h1>
            <p className={s.heroLead}>
              Ведите секцию с телефона: отмечайте посещения, следите за
              абонементами, принимайте оплату онлайн и дайте родителям кабинет с
              расписанием. Без отдельного администратора и без таблиц в конце
              дня.
            </p>
            <ul className={s.heroPoints}>
              {HERO_POINTS.map((item) => (
                <li key={item.text}>
                  <span className={s.heroPointIcon}>{item.icon}</span>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/register-clinic"
              className={`${s.btn} ${s.btnLight} ${s.btnXl}`}
              onClick={onTry}
            >
              Попробовать {TRIAL_DAYS} дней бесплатно
            </Link>
          </div>
          <div className={s.heroVisual} aria-hidden>
            <div className={s.heroCard}>
              <div className={s.heroCardTop}>
                <span />
                <span />
                <span />
              </div>
              <div className={s.heroCardRows}>
                <b />
                <b />
                <b className={s.heroCardMiss} />
                <b />
              </div>
              <div className={s.heroCardBadge}>Секция · сегодня</div>
            </div>
          </div>
        </div>
      </section>

      <section id="ways" className={s.section}>
        <div className={s.waysGrid}>
          {WAYS.map((item) => (
            <article
              key={item.id}
              className={`${s.wayCard} ${s[`way_${item.tone}`]}`}
            >
              <span className={s.wayBadge}>{item.badge}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
              <WayArt kind={item.art} />
            </article>
          ))}
        </div>
        <div className={s.waysCta}>
          <Link
            to="/register-clinic"
            className={`${s.btn} ${s.btnDark} ${s.btnXl}`}
            onClick={onTry}
          >
            Попробовать бесплатно
          </Link>
        </div>
      </section>

      <section id="advantages" className={s.section}>
        <h2 className={`${s.sectionTitle} ${s.sectionTitleCenter}`}>
          Почему тренеры уходят от тетради
        </h2>
        <div className={s.advGrid}>
          {ADVANTAGES.map((item) => (
            <article key={item.title} className={s.advCard}>
              <AdvArt kind={item.art} />
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={s.section}>
        <h2 className={`${s.sectionTitle} ${s.sectionTitleCenter}`}>
          Всё, что нужно одному тренеру
        </h2>
        <div className={s.bento}>
          <article className={s.bentoMain}>
            <h3>Абонемент сам считает занятия</h3>
            <p>
              Поставили отметку в журнале — с пакета списалось одно занятие.
              Сколько осталось, видите вы и родитель.
            </p>
            <div className={s.bentoMainArt} aria-hidden>
              <span className={s.bentoPercent}>✓</span>
              <div className={s.bentoBag} />
            </div>
          </article>
          <div className={s.bentoSide}>
            <article className={s.bentoSmall}>
              <h3>Кабинет родителей</h3>
              <p>Расписание и посещения на телефоне, без App Store.</p>
            </article>
            <article className={s.bentoSmall}>
              <h3>Онлайн-оплата</h3>
              <p>
                Подключите приём платежей — родители купят абонемент картой в
                кабинете.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className={s.section}>
        <h2 className={`${s.sectionTitle} ${s.sectionTitleCenter}`}>
          Как начать
        </h2>
        <div className={s.steps}>
          <article className={s.stepCard}>
            <span className={s.stepNum}>1</span>
            <h3>Создайте секцию</h3>
            <p>Регистрация за пару минут. Карту указывать не нужно.</p>
          </article>
          <article className={s.stepCard}>
            <span className={s.stepNum}>2</span>
            <h3>Добавьте группу</h3>
            <p>Ученики, расписание и абонементы — в одном месте.</p>
          </article>
          <article className={s.stepCard}>
            <span className={s.stepNum}>3</span>
            <h3>Отметьте занятие</h3>
            <p>С телефона на тренировке. Родители увидят посещение сами.</p>
          </article>
        </div>
      </section>

      <section id="pricing" className={s.section}>
        <h2 className={`${s.sectionTitle} ${s.sectionTitleCenter}`}>Тарифы</h2>
        <p className={`${s.sectionLead} ${s.sectionLeadCenter}`}>
          Первые {TRIAL_DAYS} дней — полный доступ. Карту указывать не нужно.
        </p>
        {plansLoading ? (
          <Preloader variant="inline" label="Загрузка тарифов…" />
        ) : (
          <div className={s.pricing}>
            {plans.map((plan) => (
              <article
                key={plan.id}
                className={`${s.priceCard} ${
                  plan.code === featuredCode ? s.priceCardOn : ""
                }`}
              >
                {plan.badge ? <span className={s.badge}>{plan.badge}</span> : null}
                <h3>{plan.name}</h3>
                {PLAN_POSITIONING[plan.code] ? (
                  <p className={s.pricePos}>{PLAN_POSITIONING[plan.code]}</p>
                ) : null}
                <p className={s.priceAmount}>
                  {formatRub(plan.monthly_price_rub)} ₽
                  <small>/ мес</small>
                </p>
                <ul>
                  {buildLandingPriceHighlights(plan.code).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <Link
                  to="/register-clinic"
                  className={`${s.btn} ${
                    plan.code === featuredCode ? s.btnDark : s.btnGhost
                  }`}
                  onClick={onTry}
                >
                  {TRIAL_DAYS} дней бесплатно
                </Link>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Вопрос-ответ</h2>
        <div className={s.faq}>
          {FAQ.map((item) => (
            <details key={item.q} className={s.faqItem}>
              <summary>
                <span>{item.q}</span>
                <span className={s.faqChevron} aria-hidden />
              </summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className={s.ctaBand}>
        <h2>Попробуйте на своей группе</h2>
        <p>
          {TRIAL_DAYS} дней бесплатно. Потом — от 790 ₽ в месяц. Карту указывать
          не нужно.
        </p>
        <Link
          to="/register-clinic"
          className={`${s.btn} ${s.btnLight} ${s.btnXl}`}
          onClick={onTry}
        >
          Попробовать бесплатно
        </Link>
      </section>

      <footer className={s.footer}>
        <LegalFooterLinks />
      </footer>

      {!hasClub ? (
        <div className={s.sticky}>
          <Link
            to="/register-clinic"
            className={`${s.btn} ${s.btnDark}`}
            onClick={onTry}
          >
            {TRIAL_DAYS} дней бесплатно
          </Link>
        </div>
      ) : null}
    </div>
  );
}
